import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, AlertCircle, ArrowRight, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Card } from '../../../../../components/ui/Card';
import { ConfirmDialog } from '../../../../../components/shared/ConfirmDialog';
import { listSnapshots, loadSnapshot, deleteSnapshot, clearAllSnapshots, getSnapshotCap, diffSnapshots, type SnapshotIndexEntry, type SnapshotDiff } from '../../../../../governance/pipeline/snapshots';
import { severityColor, severityLabel } from '../../../../../governance/utils/severity';
import { Badge } from '../../../../../components/ui/Badge';
interface CompareSnapshotsTabProps {
  refreshTrigger: number;
}
export function CompareSnapshotsTab({
  refreshTrigger
}: CompareSnapshotsTabProps) {
  const [snapshots, setSnapshots] = useState<SnapshotIndexEntry[]>([]);
  const [baseId, setBaseId] = useState<string>('');
  const [targetId, setTargetId] = useState<string>('');
  const [diff, setDiff] = useState<SnapshotDiff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingClearAll, setPendingClearAll] = useState(false);
  const cap = getSnapshotCap();
  useEffect(() => {
    const list = listSnapshots();
    setSnapshots(list);
    if (list.length >= 2 && !baseId && !targetId) {
      // Default to comparing the two most recent
      setTargetId(list[0].id);
      setBaseId(list[1].id);
    }
  }, [refreshTrigger]);
  const handleCompare = () => {
    setError(null);
    if (!baseId || !targetId) {
      setError('Selecteer twee snapshots om te vergelijken.');
      return;
    }
    if (baseId === targetId) {
      setError('Selecteer twee verschillende snapshots.');
      return;
    }
    const base = loadSnapshot(baseId);
    const target = loadSnapshot(targetId);
    if (!base || !target) {
      setError('Kon een of beide snapshots niet laden.');
      return;
    }
    // Ensure base is older than target for logical diffing
    const isBaseOlder = new Date(base.createdAt) < new Date(target.createdAt);
    const actualBase = isBaseOlder ? base : target;
    const actualTarget = isBaseOlder ? target : base;
    if (!isBaseOlder) {
      setBaseId(actualBase.id);
      setTargetId(actualTarget.id);
    }
    setDiff(diffSnapshots(actualBase, actualTarget));
  };
  const handleDelete = (id: string) => {
    setPendingDeleteId(id);
  };
  const confirmDelete = () => {
    const id = pendingDeleteId;
    if (!id) return;
    deleteSnapshot(id);
    setSnapshots(listSnapshots());
    if (baseId === id) setBaseId('');
    if (targetId === id) setTargetId('');
    if (diff?.baseId === id || diff?.targetId === id) setDiff(null);
    setPendingDeleteId(null);
  };
  const confirmClearAll = () => {
    clearAllSnapshots();
    setSnapshots([]);
    setBaseId('');
    setTargetId('');
    setDiff(null);
    setPendingClearAll(false);
  };
  const pendingSnapshot = pendingDeleteId ? snapshots.find(s => s.id === pendingDeleteId) : null;
  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="space-y-6" data-id="element-5160" data-mp="3jx5woyn5qm5n">
      <Card className="p-5" data-id="element-5161" data-mp="3k0a6cbje6a3p">
        <div className="flex flex-col md:flex-row gap-4 items-end" data-id="element-5162" data-mp="3eyukwiday1ks">
          <div className="flex-1 w-full" data-id="element-5163" data-mp="3f17cln8v3qb1">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5164" data-mp="3a7w47hoej1h0">
              Base Snapshot (Ouder)
            </label>
            <select value={baseId} onChange={e => setBaseId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-5165" data-mp="359ui0nvoim1b">
              <option value="" data-id="element-5166" data-mp="2ral5pzalgc2m">-- Selecteer --</option>
              {snapshots.map(s => <option key={s.id} value={s.id} data-id="element-5167" data-mp="v7gdwijknts2">
                  {formatDate(s.createdAt)} {s.label ? `- ${s.label}` : ''} (
                  {s.totalIssues} issues)
                </option>)}
            </select>
          </div>
          <div className="flex-1 w-full" data-id="element-5168" data-mp="lerfao94eae5">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5169" data-mp="qevjpfwr7eo0">
              Target Snapshot (Nieuwer)
            </label>
            <select value={targetId} onChange={e => setTargetId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-5170" data-mp="bh36iueoj2cz">
              <option value="" data-id="element-5171" data-mp="7hckt540fcfb">-- Selecteer --</option>
              {snapshots.map(s => <option key={s.id} value={s.id} data-id="element-5172" data-mp="1wx26b5a236sf">
                  {formatDate(s.createdAt)} {s.label ? `- ${s.label}` : ''} (
                  {s.totalIssues} issues)
                </option>)}
            </select>
          </div>
          <Button variant="primary" onClick={handleCompare} disabled={!baseId || !targetId} data-id="element-5173" data-mp="26zo36pvtr5us">
            Vergelijk
          </Button>
        </div>

        {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2 text-sm" data-id="element-5174" data-mp="2fzubzygkvhnm">
            <AlertCircle className="w-4 h-4" data-id="element-5175" data-mp="2uoqqad989qwo" />
            {error}
          </div>}

        <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-5176" data-mp="2pqoy92ttqh92">
          <div className="flex items-center justify-between mb-2" data-id="element-5177" data-mp="2ps9ucwvse891">
            <h4 className="text-sm font-medium text-gray-700" data-id="element-5178" data-mp="33onbu60bm9ni">
              Opgeslagen Snapshots ({snapshots.length}/{cap})
            </h4>
            {snapshots.length > 0 && <button onClick={() => setPendingClearAll(true)} className="text-xs font-medium text-gray-500 hover:text-red-600 transition-colors inline-flex items-center gap-1" data-id="element-5179" data-mp="19b5mcdgcv2pu">
                <Trash2 className="w-3.5 h-3.5" data-id="element-5180" data-mp="34gn1ldmx0zqr" />
                Alles wissen
              </button>}
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-5181" data-mp="39fzzquj8lt8n">
            {snapshots.map(s => <div key={s.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5 text-sm" data-id="element-5182" data-mp="3efcl1augms94">
                <span className="text-gray-600" data-id="element-5183" data-mp="3ndetgbgiwn6f">{formatDate(s.createdAt)}</span>
                {s.label && <span className="font-medium text-gray-900" data-id="element-5184" data-mp="3sb80t7tkbn8a">{s.label}</span>}
                <button onClick={() => handleDelete(s.id)} className="text-gray-400 hover:text-red-600 transition-colors ml-1" title="Verwijderen" data-id="element-5185" data-mp="z56m6s8rn7r">
                  <Trash2 className="w-3.5 h-3.5" data-id="element-5186" data-mp="auq1lkyw7n80" />
                </button>
              </div>)}
            {snapshots.length === 0 && <span className="text-sm text-gray-500 italic" data-id="element-5187" data-mp="1zh8a9cyo6x8z">
                Geen snapshots gevonden.
              </span>}
          </div>
        </div>
      </Card>

      {diff && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5188" data-mp="1qgjw8i9a0ww3">
          {/* Aggregate Deltas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-5189" data-mp="1li8oqe6geyh1">
            <DeltaStat label="Totaal Issues" delta={diff.aggregate.totalIssues} data-id="element-5190" data-mp="2j34odkxktd0p" />
            <DeltaStat label="Blockers" delta={diff.aggregate.issuesByKind.blocker} data-id="element-5191" data-mp="2e5lcgrocpcbx" />
            <DeltaStat label="Coach" delta={diff.aggregate.issuesByKind.coach} data-id="element-5192" data-mp="29c09smbtbu6e" />
            <DeltaStat label="Info" delta={diff.aggregate.issuesByKind.info} data-id="element-5193" data-mp="31udzfymzw9ue" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5194" data-mp="2x3xii4jyu63a">
            {/* Top Code Changes */}
            <Card className="p-5" data-id="element-5195" data-mp="25gmhavkh08oq">
              <h3 className="font-semibold text-gray-900 mb-4" data-id="element-5196" data-mp="25fksloaj7noj">
                Code Verschuivingen
              </h3>
              <div className="space-y-2" data-id="element-5197" data-mp="2a8dxcc602w0m">
                {diff.codeChanges.filter(c => c.delta !== 0).slice(0, 10).map(c => <div key={c.code} className="flex items-center justify-between p-2 rounded-md bg-gray-50 border border-gray-100" data-id="element-5198" data-mp="20ht0at5z5zjt">
                      <span className="font-mono text-xs text-gray-700" data-id="element-5199" data-mp="2t5f5g5uupc8s">
                        {c.code}
                      </span>
                      <div className="flex items-center gap-3 text-sm" data-id="element-5200" data-mp="2k58uaqmmkocs">
                        <span className="text-gray-500 w-8 text-right" data-id="element-5201" data-mp="2k3fo1cfxc65b">
                          {c.base}
                        </span>
                        <ArrowRight className="w-3 h-3 text-gray-400" data-id="element-5202" data-mp="18pu0fvppqclw" />
                        <span className="font-medium text-gray-900 w-8" data-id="element-5203" data-mp="18o0qo7jszc9o">
                          {c.target}
                        </span>
                        <span className={`w-12 text-right font-medium ${c.delta > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-5204" data-mp="w815422m524h">
                          {c.delta > 0 ? '+' : ''}
                          {c.delta}
                        </span>
                      </div>
                    </div>)}
                {diff.codeChanges.filter(c => c.delta !== 0).length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-5205" data-mp="clboj5bs6j0f">
                    Geen wijzigingen in issue codes.
                  </p>}
              </div>
            </Card>

            {/* Verdict Flips */}
            <Card className="p-5" data-id="element-5206" data-mp="3uxhzwlde8edt">
              <h3 className="font-semibold text-gray-900 mb-4" data-id="element-5207" data-mp="3uwgcrij3s1fq">
                Verdict Flips ({diff.verdictChanges.length})
              </h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2" data-id="element-5208" data-mp="3pwapeejv913g">
                {diff.verdictChanges.map(v => <div key={v.qid} className="flex items-center justify-between p-2 rounded-md bg-gray-50 border border-gray-100" data-id="element-5209" data-mp="1paswzqjgfkl4">
                    <span className="text-sm font-medium text-gray-700" data-id="element-5210" data-mp="1fclrq4yj2qyy">
                      {v.qid}
                    </span>
                    <div className="flex items-center gap-2 text-xs" data-id="element-5211" data-mp="1bbthb8dx6g13">
                      <VerdictBadge verdict={v.from} data-id="element-5212" data-mp="16kbf6h05u9ej" />
                      <ArrowRight className="w-3 h-3 text-gray-400" data-id="element-5213" data-mp="16iqlsupl0wi9" />
                      <VerdictBadge verdict={v.to} data-id="element-5214" data-mp="11m06i9llq4xw" />
                    </div>
                  </div>)}
                {diff.verdictChanges.length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-5215" data-mp="rmiznoum5l0c">
                    Geen vragen zijn van verdict veranderd.
                  </p>}
              </div>
            </Card>
          </div>
        </div>}

      <ConfirmDialog open={pendingDeleteId !== null} title="Snapshot verwijderen?" message={pendingSnapshot ? `Snapshot van ${formatDate(pendingSnapshot.createdAt)}${pendingSnapshot.label ? ` ("${pendingSnapshot.label}")` : ''} wordt definitief verwijderd. Dit kan niet ongedaan gemaakt worden.` : 'Deze snapshot wordt definitief verwijderd. Dit kan niet ongedaan gemaakt worden.'} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmDelete} onCancel={() => setPendingDeleteId(null)} data-id="element-5216" data-mp="2z7rareejx65x" />

      <ConfirmDialog open={pendingClearAll} title="Alle snapshots wissen?" message={`Alle ${snapshots.length} opgeslagen snapshots worden definitief verwijderd. Dit kan niet ongedaan gemaakt worden.`} confirmLabel="Alles wissen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmClearAll} onCancel={() => setPendingClearAll(false)} data-id="element-5217" data-mp="2pgwm63du1uhb" />
    </div>;
}
function DeltaStat({
  label,
  delta
}: {
  label: string;
  delta: number;
}) {
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const isZero = delta === 0;
  return <Card className="p-4 flex flex-col" data-id="element-5218" data-mp="23k3boblrhee8">
      <span className="text-sm text-gray-500 font-medium" data-id="element-5219" data-mp="1yiw5ggz9on9o">{label}</span>
      <div className="mt-2 flex items-center gap-2" data-id="element-5220" data-mp="1atbkxtngthya">
        <span className={`text-2xl font-bold ${isZero ? 'text-gray-900' : isPositive ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-5221" data-mp="1au3fr298nbni">
          {isPositive ? '+' : ''}
          {delta}
        </span>
        {!isZero && (isPositive ? <TrendingUp className="w-5 h-5 text-red-500" data-id="element-5222" data-mp="1fs4x9ir0n2wv" /> : <TrendingDown className="w-5 h-5 text-emerald-500" data-id="element-5223" data-mp="2w6xtf0r71zo0" />)}
        {isZero && <Minus className="w-5 h-5 text-gray-400" data-id="element-5224" data-mp="2w8r36ox3t008" />}
      </div>
    </Card>;
}
function VerdictBadge({
  verdict
}: {
  verdict: string;
}) {
  const colors = {
    PASS: 'bg-emerald-100 text-emerald-800',
    FAIL: 'bg-red-100 text-red-800',
    REVIEW_NEEDED: 'bg-amber-100 text-amber-800'
  };
  return <span className={`px-2 py-0.5 rounded-full font-medium ${colors[verdict as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`} data-id="element-5225" data-mp="hwsnnmpisgj6">
      {verdict}
    </span>;
}